import { ref, computed } from "vue";
import { defineStore } from "pinia";
import * as Sentry from "@sentry/vue";
import { getGroupPictureFromFirestorage } from "@/helpers/firestorage/GroupPictureService";
import { getGroupMemberProfilePictureFromFirestorage } from "@/helpers/firestorage/GroupMemberPictureService";
import { fetchGroupsById } from "@/helpers/firestore/GroupService";
import { handleFirestoreException } from "@/helpers/exceptions/handler";
import {
  getObjectPathValue,
  setObjectPathValue,
} from "@/helpers/utils/ObjectPath";
import {
  getGroupsFromLocalStorage,
  setGroupsInLocalStorage,
} from "@/helpers/utils/local-storage";
import {
  clearCachedImages,
  onCacheInvalidated,
} from "@/helpers/cache/image-cache";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useUserStore } from "@/stores/modules/user/user";
import type { GroupDocument } from "@kekkle/shared/types/documents/group-document/group-document";
import type { GroupMember } from "@kekkle/shared/types/fields/group-member/group-member";
import { FirestoreError } from "firebase/firestore";
import { ImageSizes } from "@/enums/images-sizes";
import type { GroupPermission } from "@kekkle/shared/enums/group-permission";

type PictureData = {
  url: string;
  blob?: Blob;
  loading?: boolean;
  fromCache?: boolean;
};

type GroupPictureDataStructure = {
  [groupId: string]: {
    blurhash: string;
  } & {
    [K in ImageSizes]?: PictureData;
  };
};

type MemberPictureDataStructure = {
  [groupId: string]: {
    [uuid: string]: {
      [format: string]: PictureData;
    };
  };
};

type Birthday = {
  uuid: string;
  birthday: Date;
  name: string;
};

type MemberWithUuid = GroupMember & {
  uuid: string;
  groupId: string;
  permission: GroupPermission;
};

/**
 * Groups store managing group data, members, and pictures
 * Uses Pinia with Composition API and TypeScript
 */
export const useGroupsStore = defineStore("groups", () => {
  const authStore = useAuthStore();
  const userStore = useUserStore();

  /* State */
  const groupsLoading = ref<boolean>(false);
  const groupsFailed = ref<boolean>(false);
  const userGroups = ref<Map<string, GroupDocument>>(
    getGroupsFromLocalStorage(),
  );
  const groupPictureData = ref<GroupPictureDataStructure>({});
  const groupMembersProfilePictureData = ref<MemberPictureDataStructure>({});

  /* Computed / Getters */
  const activeGroupId = computed(() => userStore.activeGroup);

  const groupData = computed<GroupDocument | undefined>(() => {
    if (!userGroups.value || !activeGroupId.value) {
      return undefined;
    }
    return userGroups.value.get(activeGroupId.value);
  });

  const groupIcalToken = computed(() => groupData.value?.ical_token);

  const activeGroupMembers = computed(() => groupData.value?.members);

  const activeGroupMembersSorted = computed<(GroupMember & { uuid: string })[]>(
    () => {
      const members: Record<string, GroupMember> =
        activeGroupMembers.value || {};
      return Object.entries(members)
        .map(([uuid, member]: [string, GroupMember]) => ({ ...member, uuid }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  );

  const allGroupMembers = computed<Record<string, MemberWithUuid>>(() => {
    const members: Record<string, MemberWithUuid> = {};
    const groups: GroupDocument[] = Array.from(
      userGroups.value?.values() || [],
    );
    groups.forEach((group: GroupDocument) => {
      if (group.members) {
        const memberEntries: [string, GroupMember][] = Object.entries(
          group.members,
        );
        memberEntries.forEach(([uuid, member]: [string, GroupMember]) => {
          members[uuid] = {
            ...(member as GroupMember),
            groupId: group._id,
            uuid: uuid,
          } as MemberWithUuid;
        });
      }
    });
    return members;
  });

  const allGroupMembersUuids = computed(() =>
    Object.keys(allGroupMembers.value),
  );

  const birthdays = computed((): Birthday[] => {
    const members = activeGroupMembers.value || {};
    const birthdaysList: Birthday[] = [];
    for (const [uuid, member] of Object.entries(members)) {
      if (member.birthday) {
        birthdaysList.push({
          uuid,
          birthday: member.birthday,
          name: member.name,
        });
      }
    }
    return birthdaysList;
  });

  const activeGroupPictureData = computed<
    | ({
        blurhash: string;
      } & {
        [K in ImageSizes]?: PictureData;
      })
    | undefined
  >(() => {
    if (!activeGroupId.value) return undefined;
    return groupPictureData.value[activeGroupId.value];
  });

  const activeGroupMembersProfilePictureData = computed<
    | {
        [uuid: string]: {
          [format: string]: PictureData;
        };
      }
    | undefined
  >(() => {
    if (!activeGroupId.value) return undefined;
    return getObjectPathValue(groupMembersProfilePictureData.value, [
      activeGroupId.value,
    ]);
  });

  const isAdmin = computed<boolean>(() => {
    const uuid: string | undefined = authStore.authUuid;
    if (!uuid || !activeGroupId.value) return false;
    return (
      activeGroupMembers.value?.[uuid]?.permission === "admin" &&
      authStore.authIdTokenClaims?.[activeGroupId.value] === "admin"
    );
  });

  const isOrganiser = computed<boolean>(() => {
    const uuid: string | undefined = authStore.authUuid;
    if (!uuid || !activeGroupId.value) return false;
    return (
      activeGroupMembers.value?.[uuid]?.permission === "organiser" &&
      authStore.authIdTokenClaims?.[activeGroupId.value] === "organiser"
    );
  });

  const isMember = computed<boolean>(() => {
    const uuid: string | undefined = authStore.authUuid;
    if (!uuid || !activeGroupId.value) return false;
    return (
      activeGroupMembers.value?.[uuid]?.permission === "member" &&
      authStore.authIdTokenClaims?.[activeGroupId.value] === "member"
    );
  });

  /* Actions */
  function setGroupsLoading(loading: boolean): void {
    groupsLoading.value = loading;
  }

  function setGroupsFailed(failed: boolean): void {
    groupsFailed.value = failed;
  }

  function setUserGroups(groups: GroupDocument[]): void {
    const groupsMap: Map<string, GroupDocument> = new Map<
      string,
      GroupDocument
    >();
    groups.forEach((group: GroupDocument) => {
      groupsMap.set(group._id, group);
    });
    userGroups.value = groupsMap;
    setGroupsInLocalStorage(groupsMap);

    // Set blurhash for each group
    groups.forEach((group: GroupDocument) => {
      setObjectPathValue(
        groupPictureData.value,
        [group._id, "blurhash"],
        group?.group_picture_blurhash,
      );
    });
  }

  function setSingleGroup(group: GroupDocument, groupId: string): void {
    userGroups.value.set(groupId, group);
    setGroupsInLocalStorage(userGroups.value);

    // Update blurhash
    const groups: GroupDocument[] = Array.from(userGroups.value.values());
    groups.forEach((g: GroupDocument) => {
      setObjectPathValue(
        groupPictureData.value,
        [g._id, "blurhash"],
        g?.group_picture_blurhash,
      );
    });
  }

  function setGroupPictureData(params: {
    format: string;
    url: string;
    blob: Blob;
    groupId: string;
    fromCache?: boolean;
  }): void {
    setObjectPathValue(
      groupPictureData.value,
      [params.groupId, params.format],
      {
        url: params.url,
        blob: params.blob,
        fromCache: params.fromCache ?? false,
      },
    );
  }

  function setGroupPictureLoading(params: {
    format: string;
    loading: boolean;
    groupId: string;
  }): void {
    setObjectPathValue(
      groupPictureData.value,
      [params.groupId, params.format, "loading"],
      params.loading,
    );
  }

  function setGroupMembersProfilePictureData(params: {
    uuid: string;
    url: string;
    format: string;
    groupId: string;
    fromCache?: boolean;
  }): void {
    setObjectPathValue(
      groupMembersProfilePictureData.value,
      [params.groupId, params.uuid, params.format],
      { url: params.url, loading: false, fromCache: params.fromCache ?? false },
    );
  }

  function setGroupMembersProfilePictureLoading(params: {
    uuid: string;
    format: string;
    loading: boolean;
    groupId: string;
  }): void {
    setObjectPathValue(
      groupMembersProfilePictureData.value,
      [params.groupId, params.uuid, params.format, "loading"],
      params.loading,
    );
  }

  function reset(): void {
    userGroups.value = new Map();
    groupsLoading.value = false;
    groupsFailed.value = false;
    groupPictureData.value = {};
    groupMembersProfilePictureData.value = {};
    setGroupsInLocalStorage(new Map());
  }

  async function clearGroupProfilePictureData(
    groupId?: string,
    refetch: boolean = false,
  ): Promise<void> {
    if (groupId) {
      setObjectPathValue(groupPictureData.value, [groupId], {});
      await clearCachedImages(`group:${groupId}`).catch((error) => {
        console.error("Error clearing group picture cache:", error);
      });

      // Optionally refetch after clearing
      if (refetch && groupId === activeGroupId.value) {
        await downloadGroupPictureData(ImageSizes.Thumb);
        await downloadGroupPictureData(ImageSizes.SD);
        await downloadGroupPictureData(ImageSizes.HD);
      }
    } else {
      groupPictureData.value = {};
    }
  }

  function clearGroupMemberProfilePictureData(params: {
    uuid: string;
    groupId: string;
  }): void {
    setObjectPathValue(
      groupMembersProfilePictureData.value,
      [params.groupId, params.uuid],
      {},
    );

    if (params.groupId && params.uuid) {
      clearCachedImages(`group-member:${params.groupId}:${params.uuid}`).catch(
        (error) => {
          console.error(
            "Error clearing group member profile picture cache:",
            error,
          );
        },
      );
    }
  }

  async function refreshSingleGroup(groupId: string): Promise<void> {
    try {
      const fetchedGroups: GroupDocument[] = await fetchGroupsById([groupId]);
      const groupData: GroupDocument | undefined = fetchedGroups.find(
        (group: GroupDocument) => group._id === groupId,
      );
      if (!groupData) {
        return;
      }
      setSingleGroup(groupData, groupId);
    } catch (e) {
      handleFirestoreException({ e: e as FirestoreError });
    }
  }

  async function refreshUserGroups(groupIds: string[]): Promise<void> {
    try {
      const fetchedGroups: GroupDocument[] = await fetchGroupsById(groupIds);
      setUserGroups(fetchedGroups);
    } catch (e) {
      handleFirestoreException({ e: e as FirestoreError });
    }
  }

  async function downloadGroupPictureData(
    format: ImageSizes = ImageSizes.HD,
  ): Promise<void> {
    const groupId: string | null = activeGroupId.value;
    if (!groupId) return;

    const pictureData: PictureData | undefined = groupPictureData.value?.[
      groupId
    ]?.[format] as PictureData | undefined;
    if (pictureData?.loading === true) {
      return;
    }

    if (!groupData.value?.group_picture) {
      return;
    }

    setGroupPictureLoading({ loading: true, groupId, format });

    try {
      const result: {
        url: string;
        blob: Blob;
        format: string;
        fromCache: boolean;
      } = await getGroupPictureFromFirestorage(
        groupId,
        groupData.value.group_picture,
        format,
      );

      setGroupPictureData({
        format: result.format,
        blob: result.blob,
        url: result.url,
        groupId: groupId,
        fromCache: result.fromCache,
      });
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      setGroupPictureLoading({ loading: false, groupId, format });
    }
  }

  async function downloadGroupMemberProfilePictureData(params: {
    uuid: string;
    format?: ImageSizes;
    groupId?: string;
  }): Promise<void> {
    console.log("going to download", params.format);
    const format: ImageSizes = params.format || ImageSizes.SD;
    const groupId: string | null = params.groupId || activeGroupId.value;
    if (!groupId) return;

    const memberPictureData: PictureData | undefined =
      groupMembersProfilePictureData.value[groupId]?.[params.uuid]?.[format];
    if (memberPictureData?.loading === true) {
      return;
    }

    setGroupMembersProfilePictureLoading({
      uuid: params.uuid,
      format: format,
      loading: true,
      groupId: groupId,
    });

    try {
      const result: {
        url: string;
        format: string;
        fromCache: boolean;
      } = await getGroupMemberProfilePictureFromFirestorage(
        groupId,
        params.uuid,
        format,
      );

      setGroupMembersProfilePictureData({
        uuid: params.uuid,
        url: result.url,
        format: format,
        groupId: groupId,
        fromCache: result.fromCache,
      });
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setGroupMembersProfilePictureLoading({
        uuid: params.uuid,
        format: format,
        loading: false,
        groupId: groupId,
      });
    }
  }

  /**
   * Refresh current active group data and download pictures
   */
  async function refreshCurrentGroupData(): Promise<void> {
    const groupId: string | null = activeGroupId.value;
    if (!groupId) {
      return;
    }
    await refreshSingleGroup(groupId);
    await downloadGroupPictureData(ImageSizes.SD);
    await downloadGroupPictureData(ImageSizes.Thumb);
  }

  /**
   * Refresh all user groups and validate active group membership
   * Clears group data and redirects if active group is invalid
   */
  async function refreshAllUserGroupsData(): Promise<void> {
    const authUuid: string | undefined = authStore.authUuid;
    const userGroupIds: string[] | undefined = userStore.userData?.groups;

    if (!authUuid || !userGroupIds) {
      return;
    }

    await refreshUserGroups(userGroupIds);
    await downloadGroupPictureData(ImageSizes.SD);
    await downloadGroupPictureData(ImageSizes.Thumb);

    // Check if the active group is still valid
    const currentActiveGroupId: string | null = activeGroupId.value;
    if (currentActiveGroupId && !userGroupIds.includes(currentActiveGroupId)) {
      reset();

      if (!userStore.activeGroup) {
        const router = await import("@/router/router");
        await router.default.push({ name: "GroupSelector" });
      }
    }
  }

  /* Register cache invalidation callback */
  onCacheInvalidated((keyPattern) => {
    // Parse the key pattern to extract cache type and group ID
    // Pattern format: "group:groupId:picturePath" or "group-member:groupId:uuid"
    if (
      keyPattern.startsWith("group:") &&
      !keyPattern.includes("group-member")
    ) {
      const parts = keyPattern.split(":");
      const groupId = parts[1];

      if (groupId) {
        // Get the existing formats before clearing
        const existingFormats = Object.keys(
          groupPictureData.value[groupId] || {},
        ).filter((key) => key !== "blurhash");

        // Clear store data for this group
        setObjectPathValue(groupPictureData.value, [groupId], {});

        // Refetch only the sizes that were previously loaded
        if (groupId === activeGroupId.value) {
          existingFormats.forEach((format) => {
            downloadGroupPictureData(format as ImageSizes);
          });
        }
      }
    } else if (keyPattern.startsWith("group-member:")) {
      const parts = keyPattern.split(":");
      const groupId = parts[1];
      const uuid = parts[2];

      if (groupId && uuid) {
        // Get the existing formats before clearing
        const existingFormats = Object.keys(
          groupMembersProfilePictureData.value[groupId]?.[uuid] || {},
        );

        // Clear store data for this member
        setObjectPathValue(
          groupMembersProfilePictureData.value,
          [groupId, uuid],
          {},
        );

        // Refetch only the sizes that were previously loaded
        if (groupId === activeGroupId.value) {
          existingFormats.forEach((format) => {
            downloadGroupMemberProfilePictureData({
              uuid,
              format: format as ImageSizes,
            });
          });
        }
      }
    }
  });

  /* Exposed store */
  return {
    // State
    groupsLoading,
    groupsFailed,
    userGroups,
    groupPictureData,
    groupMembersProfilePictureData,

    // Computed
    groupData,
    groupIcalToken,
    activeGroupMembers,
    activeGroupMembersSorted,
    allGroupMembers,
    allGroupMembersUuids,
    birthdays,
    activeGroupPictureData,
    activeGroupMembersProfilePictureData,
    isAdmin,
    isOrganiser,
    isMember,

    // Actions
    setGroupsLoading,
    setGroupsFailed,
    setUserGroups,
    setSingleGroup,
    setGroupPictureData,
    setGroupPictureLoading,
    setGroupMembersProfilePictureData,
    setGroupMembersProfilePictureLoading,
    reset,
    clearGroupProfilePictureData,
    clearGroupMemberProfilePictureData,
    refreshSingleGroup,
    refreshUserGroups,
    refreshCurrentGroupData,
    refreshAllUserGroupsData,
    downloadGroupPictureData,
    downloadGroupMemberProfilePictureData,
  };
});
